import React, { useEffect, useState } from "react";
import { Table, Button, Popconfirm, Row, Col, PaginationProps, TablePaginationConfig, Upload, Input, Form, message, Card, Select } from "antd";
import { UploadOutlined, PlusOutlined } from "@ant-design/icons";
import { ComponentTitle, PageTitleLastCol, StledSpin } from "../custom-components";
import { useOutletContext } from "react-router";
import { authAxios } from "../utils/session_utils";
import { makeKeyInLowerCaseHelper } from "../utils";
import { RcFile } from "antd/es/upload";

// Layout options come from the backend so a new preset only needs a server
// deploy — no frontend change required. Falls back to a single "Tamron Hall"
// option if the fetch hasn't returned yet.
const DEFAULT_LAYOUT = "tamron-hall";
const FALLBACK_LAYOUT_OPTIONS = [
	{ value: DEFAULT_LAYOUT, label: "Tamron Hall" },
];

interface LayoutOption {
	value: string;
	label: string;
}

interface Program {
	id: string;
	title: string;
	s3_key: string;
	filename: string;
	post_sheet_layout?: string;
}

export const initialPaginationObj: TablePaginationConfig = {
	current: 1,
	pageSize: 10,
	position: ["bottomRight"],
	hideOnSinglePage: false,
	showSizeChanger: true,
	pageSizeOptions: [10, 20],
};

const ProgramsListing: React.FC = () => {
	const { user, setUser }: any = useOutletContext();

	const [programs, setPrograms] = useState<Program[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [showUploadForm, setShowUploadForm] = useState<boolean>(false);
	const [uploadTitle, setUploadTitle] = useState<string>('');
	const [uploadLayout, setUploadLayout] = useState<string>(DEFAULT_LAYOUT);
	const [fileList, setFileList] = useState<RcFile[]>([]);
	const [isUploading, setIsUploading] = useState<boolean>(false);
	const [savingLayoutFor, setSavingLayoutFor] = useState<string | null>(null);
	const [layoutOptions, setLayoutOptions] = useState<LayoutOption[]>(FALLBACK_LAYOUT_OPTIONS);

	const [pagination, setPagination] = useState<PaginationProps>({
		...initialPaginationObj,
		pageSize: user.page_sizes?.programs_listing ?? 10,
	});

	useEffect(() => {
		fetchPrograms();
	}, [pagination.current, pagination.pageSize]);

	useEffect(() => {
		const fetchLayouts = async () => {
			try {
				const response = await authAxios({
					method: "GET",
					url: `${process.env.REACT_APP_BACK_END_API}/auto-podder/post-sheet-layouts`,
					headers: { "Content-Type": "application/json" },
				});
				const layouts = response.data?.layouts || [];
				if (layouts.length > 0) {
					setLayoutOptions(
						layouts.map((l: { key: string; displayName: string }) => ({
							value: l.key,
							label: l.displayName,
						}))
					);
				}
			} catch (err) {
				console.error("Failed to load post-sheet layouts; using fallback", err);
			}
		};
		fetchLayouts();
	}, []);

	const fetchPrograms = async () => {
		setLoading(true);
		try {
			const url = `${
				process.env.REACT_APP_BACK_END_API
			}/auto-podder/programs?pagination=${JSON.stringify(pagination)}`;
			const config: any = {
				method: "GET",
				url,
				headers: {
					"Content-Type": "application/json",
				},
			};
			const response = await authAxios(config);
			console.log(response.data.rows );

      const convertedObject = await makeKeyInLowerCaseHelper(response.data.rows);
      console.log({ convertedObject });
			setPrograms(convertedObject.map((item: any) => ({ ...item })));
			setPagination({ ...pagination, total: response.data.total });
			setLoading(false);
		} catch (error) {
			console.error({ error });
		}
	};

	const removeProgram = async (id: string) => {
		try {
      const url = `${process.env.REACT_APP_BACK_END_API}/auto-podder/programs/${id}`;
      const config: any = {
        method: "DELETE",
        url,
        headers: {
          "Content-Type": "application/json",
        },
      };
      await authAxios(config);
			fetchPrograms(); // Refresh the list after deletion
		} catch (error) {
			console.error("Error deleting program:", error);
		}
	};

	const fetchSignedUrl = async (s3Key: string) => {
		try {
      const url = `${process.env.REACT_APP_BACK_END_API}/auto-podder/programs/signed-url?s3Key=${encodeURIComponent(s3Key)}`;
      const config: any = {
        method: "GET",
        url,
        headers: {
          "Content-Type": "application/json",
        },
      };
      const response = await authAxios(config);
			window.open(response.data.url, "_blank");
		} catch (error) {
			console.error("Error fetching signed URL:", error);
		}
	};

	const uploadFile = async () => {
		const formData = new FormData();
		formData.append("file1", fileList[0] as RcFile);
		formData.append("title", uploadTitle);
		formData.append("post_sheet_layout", uploadLayout);

		try {
			setIsUploading(true);
			const config = {
				method: "POST",
				url: `${process.env.REACT_APP_BACK_END_API}/auto-podder/upload-program`,
				data: formData,
				headers: {
					"Content-Type": "multipart/form-data",
				},
			};
			const response = await authAxios(config);
			console.log('Response:', response.data);
			if (!response.data.error) {
				message.success(response.data.message);
				setShowUploadForm(false);
				setFileList([]);
				setUploadTitle('');
				setUploadLayout(DEFAULT_LAYOUT);
				fetchPrograms(); // Refresh the list after successful upload
			} else {
				message.error(response.data.error);
			}
		} catch (error: any) {
			console.log('Error:', error);
			console.error(error.response?.data?.error);
			message.error(error.response?.data?.error || 'Something went wrong during upload.');
		} finally {
			setIsUploading(false);
		}
	};

	const updateProgramLayout = async (programId: string, layout: string) => {
		setSavingLayoutFor(programId);
		try {
			const config = {
				method: "PATCH",
				url: `${process.env.REACT_APP_BACK_END_API}/auto-podder/programs/${programId}/layout`,
				data: { post_sheet_layout: layout },
				headers: { "Content-Type": "application/json" },
			};
			await authAxios(config);
			setPrograms((prev) =>
				prev.map((p) =>
					p.id === programId ? { ...p, post_sheet_layout: layout } : p
				)
			);
			message.success("Layout updated");
		} catch (err: any) {
			console.error("Error updating layout:", err);
			message.error(err.response?.data?.message || err.response?.data?.err || "Failed to update layout");
		} finally {
			setSavingLayoutFor(null);
		}
	};

	const handleUploadSubmit = async () => {
		if (!uploadTitle || fileList.length === 0) {
			message.error('Please provide a title and upload a CSV file.');
			return;
		}
		if (fileList.length > 1) {
			message.error('Please upload only one CSV file.');
			return;
		}
		const file = fileList[0];
		// Validate file type
		if (file.type !== 'text/csv') {
			message.error('Please upload a valid CSV file.');
			return;
		}

		await uploadFile();
	};

	const beforeUpload = (file: RcFile) => {
		setFileList([file]);
		return false;
	};

	const columns = [
		{
			title: "Program Name",
			dataIndex: "title",
			key: "title",
		},
		{
			title: "File Name",
			dataIndex: "filename",
			key: "filename",
			render: (text: string, record: Program) => (
				<Button type="link" onClick={() => fetchSignedUrl(record.s3_key)}>
					{text}
				</Button>
			),
		},
		{
			title: "Post Sheet Layout",
			dataIndex: "post_sheet_layout",
			key: "post_sheet_layout",
			render: (text: string, record: Program) => (
				<Select
					value={record.post_sheet_layout || DEFAULT_LAYOUT}
					options={layoutOptions}
					style={{ minWidth: 220 }}
					loading={savingLayoutFor === record.id}
					disabled={savingLayoutFor === record.id}
					onChange={(value) => updateProgramLayout(record.id, value)}
				/>
			),
		},
		{
			title: "Action",
			key: "action",
			render: (text: string, record: Program) => (
				<Popconfirm
					title="Are you sure to delete this program?"
					onConfirm={() => removeProgram(record.id)}
				>
					<Button type="link" danger>
						Delete
					</Button>
				</Popconfirm>
			),
		},
	];

	return (
		<>
			<Row justify="space-between" align="middle">
				<Col>
					<ComponentTitle>Programs</ComponentTitle>
				</Col>
				<PageTitleLastCol>
					<Button 
						type="primary" 
						icon={<PlusOutlined />} 
						onClick={() => setShowUploadForm(!showUploadForm)}
					>
						{showUploadForm ? 'Hide Upload Form' : 'Upload Program'}
					</Button>
				</PageTitleLastCol>
			</Row>

			{showUploadForm && (
				<StledSpin spinning={isUploading}>
					<Row style={{ marginBottom: 24 }}>
						<Col span={24}>
							<Card
								title="Upload Program Structure File"
								actions={[
									<Button key="cancel-btn" onClick={() => {
										setShowUploadForm(false);
										setFileList([]);
										setUploadTitle('');
									}}>
										Cancel
									</Button>,
									<Button key="submit-btn" type="primary" onClick={handleUploadSubmit}>
										Submit
									</Button>,
								]}
							>
								<Form layout="vertical" onFinish={handleUploadSubmit}>
									<Form.Item label="Title" required>
										<Input
											value={uploadTitle}
											onChange={(e) => setUploadTitle(e.target.value)}
										/>
									</Form.Item>
									<Form.Item label="Post Sheet Layout" required>
										<Select
											value={uploadLayout}
											onChange={(value) => setUploadLayout(value)}
											options={layoutOptions}
										/>
									</Form.Item>
									<Form.Item label="Upload CSV" required>
										<Upload
											fileList={fileList}
											accept=".csv"
											beforeUpload={beforeUpload}
											onRemove={() => setFileList([])}
										>
											<Button icon={<UploadOutlined />}>Click to Upload</Button>
										</Upload>
									</Form.Item>
								</Form>
							</Card>
						</Col>
					</Row>
				</StledSpin>
			)}

			<Table
				columns={columns}
				dataSource={programs}
				loading={loading}
				pagination={{
					current: pagination.current,
					pageSize: pagination.pageSize,
					total: pagination.total,
					onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
				}}
				rowKey="id"
			/>
		</>
	);
};

export default ProgramsListing;
